// Personal Forecast Calculation Utilities
// Progressive disclosure: Simple calculations work, advanced users can customize

import { BiweeklyPaySchedule } from '../types/personalForecast';

// Kept locally since the old wizard types were removed
interface AmortizationSchedule {
  principal: number;
  interestRate: number;
  termMonths: number;
  monthlyPayment: number;
  schedule: Array<{ month: number; payment: number; principal: number; interest: number; balance: number }>;
}

/**
 * Calculate biweekly pay schedule for the next 12-60 months
 * Shows which months get 3 paychecks (Samantha's "extra paycheck months")
 */
export function calculateBiweeklySchedule(
  firstPayDate: string,
  paycheckAmount: number,
  months: number = 12
): BiweeklyPaySchedule {
  const firstDate = new Date(firstPayDate);
  const payDates: Date[] = [];

  // Generate all pay dates for the period
  let currentDate = new Date(firstDate);
  const endDate = new Date(firstDate);
  endDate.setMonth(endDate.getMonth() + months);

  while (currentDate <= endDate) {
    payDates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 14); // Add 2 weeks
  }

  // Group by month
  const monthlyBreakdown: Array<{
    month: string;
    paychecks: number;
    totalAmount: number;
    isExtraPaycheckMonth: boolean;
  }> = [];

  for (let i = 0; i < months; i++) {
    const targetMonth = new Date(firstDate);
    targetMonth.setMonth(targetMonth.getMonth() + i);
    const monthStr = targetMonth.toISOString().slice(0, 7); // YYYY-MM

    const paychecksThisMonth = payDates.filter(date => {
      return date.getFullYear() === targetMonth.getFullYear() &&
             date.getMonth() === targetMonth.getMonth();
    }).length;

    monthlyBreakdown.push({
      month: monthStr,
      paychecks: paychecksThisMonth,
      totalAmount: paychecksThisMonth * paycheckAmount,
      isExtraPaycheckMonth: paychecksThisMonth === 3
    });
  }

  return {
    frequency: 'biweekly',
    firstPayDate,
    paycheckAmount,
    annualPaychecks: 26,
    monthlyBreakdown
  };
}

/**
 * Calculate debt amortization schedule
 * Simple mode: just show payoff date and monthly payment
 * Advanced mode: show full amortization table
 */
export function calculateAmortization(
  principal: number,
  annualInterestRate: number,
  monthlyPayment: number
): AmortizationSchedule {
  const monthlyRate = annualInterestRate / 12 / 100;
  const schedule: AmortizationSchedule['schedule'] = [];

  let balance = principal;
  let month = 0;

  // Calculate term if not making minimum payments
  while (balance > 0.01 && month < 600) { // Max 50 years safety
    month++;

    const interestPayment = balance * monthlyRate;
    const principalPayment = Math.min(monthlyPayment - interestPayment, balance);

    // Handle case where payment doesn't cover interest (debt grows)
    if (principalPayment <= 0) {
      console.log('Monthly payment too low - debt will grow!');
      break;
    }

    balance -= principalPayment;

    schedule.push({
      month,
      payment: monthlyPayment,
      principal: principalPayment,
      interest: interestPayment,
      balance: Math.max(balance, 0)
    });
  }

  return {
    principal,
    interestRate: annualInterestRate,
    termMonths: month,
    monthlyPayment,
    schedule
  };
}

/**
 * Calculate credit card interest (end-of-month balance method)
 * Simpler and more conservative than average daily balance
 */
export function calculateCreditCardInterest(
  balance: number,
  annualRate: number,
  payment: number,
  newCharges: number = 0
): {
  interest: number;
  newBalance: number;
  principalPaid: number;
} {
  const monthlyRate = annualRate / 12 / 100;

  // Simple method: interest on end-of-month balance
  // Step 1: Apply payment
  const balanceAfterPayment = balance - payment;

  // Step 2: Add new charges
  const balanceWithCharges = balanceAfterPayment + newCharges;

  // Step 3: Calculate interest on this balance
  const interest = balanceWithCharges * monthlyRate;

  // Step 4: New balance includes interest
  const newBalance = balanceWithCharges + interest;

  const principalPaid = payment - (balance * monthlyRate);

  return {
    interest,
    newBalance,
    principalPaid: Math.max(principalPaid, 0)
  };
}

/**
 * Calculate investment growth (start-of-month balance method)
 * Simple: assumes growth on opening balance only
 * Advanced: can add contributions mid-month
 */
export function calculateInvestmentGrowth(
  openingBalance: number,
  annualReturnRate: number,
  monthlyContribution: number = 0,
  contributionTiming: 'start' | 'end' | 'mid' = 'start'
): {
  growth: number;
  contributionGrowth: number;
  closingBalance: number;
} {
  const monthlyRate = annualReturnRate / 12 / 100;

  // Growth on opening balance
  const openingGrowth = openingBalance * monthlyRate;

  // Growth on contribution (depends on timing)
  let contributionGrowth = 0;
  if (contributionTiming === 'start') {
    contributionGrowth = monthlyContribution * monthlyRate;
  } else if (contributionTiming === 'mid') {
    contributionGrowth = monthlyContribution * monthlyRate * 0.5;
  }
  // If 'end', contribution doesn't grow this month

  const totalGrowth = openingGrowth + contributionGrowth;
  const closingBalance = openingBalance + monthlyContribution + totalGrowth;

  return {
    growth: totalGrowth,
    contributionGrowth,
    closingBalance
  };
}

/**
 * Calculate debt payoff using Avalanche strategy
 * (Highest interest rate first - mathematically optimal)
 */
export function calculateDebtAvalanche(
  debts: Array<{
    id: string;
    balance: number;
    interestRate: number;
    minimumPayment: number;
  }>,
  extraPayment: number
): Array<{
  id: string;
  payment: number;
}> {
  // Sort by interest rate (highest first)
  const sorted = [...debts].sort((a, b) => b.interestRate - a.interestRate);

  const payments = sorted.map(debt => ({
    id: debt.id,
    payment: debt.minimumPayment
  }));

  // Apply extra payment to highest rate debt
  if (sorted.length > 0) {
    const highestRateDebt = payments.find(p => p.id === sorted[0].id);
    if (highestRateDebt) {
      highestRateDebt.payment += extraPayment;
    }
  }

  return payments;
}

/**
 * Calculate debt payoff using Snowball strategy
 * (Smallest balance first - psychological wins)
 */
export function calculateDebtSnowball(
  debts: Array<{
    id: string;
    balance: number;
    interestRate: number;
    minimumPayment: number;
  }>,
  extraPayment: number
): Array<{
  id: string;
  payment: number;
}> {
  // Sort by balance (smallest first)
  const sorted = [...debts].sort((a, b) => a.balance - b.balance);

  const payments = sorted.map(debt => ({
    id: debt.id,
    payment: debt.minimumPayment
  }));

  // Apply extra payment to smallest balance debt
  if (sorted.length > 0) {
    const smallestDebt = payments.find(p => p.id === sorted[0].id);
    if (smallestDebt) {
      smallestDebt.payment += extraPayment;
    }
  }

  return payments;
}

/**
 * Calculate emergency fund status
 */
export function calculateEmergencyFund(
  monthlyExpenses: number,
  liquidAssets: number,
  targetMonths: number = 6
): {
  currentMonths: number;
  targetAmount: number;
  currentAmount: number;
  percentComplete: number;
  monthlyToReachTarget: (months: number) => number;
} {
  const targetAmount = monthlyExpenses * targetMonths;
  const currentMonths = monthlyExpenses > 0 ? liquidAssets / monthlyExpenses : 0;
  const percentComplete = targetAmount > 0 ? (liquidAssets / targetAmount) * 100 : 0;

  const monthlyToReachTarget = (timeframeMonths: number) => {
    if (timeframeMonths <= 0) return 0;
    const shortfall = targetAmount - liquidAssets;
    return Math.max(shortfall / timeframeMonths, 0);
  };

  return {
    currentMonths,
    targetAmount,
    currentAmount: liquidAssets,
    percentComplete: Math.min(percentComplete, 100),
    monthlyToReachTarget
  };
}

/**
 * Project account balance forward one month
 * Handles income, expenses, interest, and investment growth
 */
export function projectAccountMonth(
  openingBalance: number,
  income: number,
  expenses: number,
  interestRate: number = 0,
  isInvestment: boolean = false,
  investmentReturnRate: number = 0
): {
  income: number;
  expenses: number;
  interest: number;
  growth: number;
  closingBalance: number;
  hasOverdraft: boolean;
} {
  let balance = openingBalance;

  // Add income
  balance += income;

  // Subtract expenses
  balance -= expenses;

  // Calculate interest (for savings accounts or debt)
  const monthlyRate = interestRate / 12 / 100;
  let interest = 0;
  if (interestRate !== 0) {
    if (balance > 0) {
      // Positive balance earns interest
      interest = balance * Math.abs(monthlyRate);
    } else {
      // Negative balance pays interest (overdraft)
      interest = balance * Math.abs(monthlyRate);
    }
    balance += interest;
  }

  // Calculate investment growth (separate from interest)
  let growth = 0;
  if (isInvestment && investmentReturnRate !== 0) {
    const growthRate = investmentReturnRate / 12 / 100;
    growth = openingBalance * growthRate; // Growth on opening balance only
    balance += growth;
  }

  return {
    income,
    expenses,
    interest,
    growth,
    closingBalance: balance,
    hasOverdraft: balance < 0
  };
}

/**
 * Calculate net worth
 */
export function calculateNetWorth(
  assets: number,
  liabilities: number
): {
  assets: number;
  liabilities: number;
  netWorth: number;
} {
  return {
    assets,
    liabilities,
    netWorth: assets - liabilities
  };
}
